import { css, html, TemplateResult } from "lit";
import { customElement } from "lit/decorators.js";
import { SugarModel } from "../loader/SugarModel";
import { SugarModelConfigurator } from "../loader/SugarModelConfigurator";
import { t } from "../translation/i18n";
import { BaseElement } from "../ui/base/BaseElement";
import { getConfiguratorData, CONFIGURATOR_MODEL_TYPE } from "./ConfiguratorData";
import { observeContext } from "./SugarConfiguratorContext";
import { logger } from "../util/Logger";
import("../configurator/SugarDebugScene");

const styles = css`
  .sc-section {
    margin-bottom: 24px;
    border-bottom: 1px solid #ddd;
    padding: 0 16px 16px;
    position: relative;
  }

  .sc-arrow {
    position: absolute;
    right: -16px;
    top: 50%;
    transform: translateY(-50%) translateX(-20px);
  }

  .sc-title {
    margin-bottom: 12px;
    font-size: 12px;
    font-weight: 700;
    line-height: 14px;
  }

  .sc-title span {
    font-size: 11px;
    font-weight: 100;
    line-height: 13px;
  }

  .sc-title.header {
    font-size: 16px;
  }

  .sc-description {
    font-weight: 100;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0.5px;
  }

  .sc-options {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: flex-start;
  }

  .sc-option-title {
    font-size: 10px;
    text-align: center;
    max-width: 50px;
  }

  .sc-circle,
  .sc-circle-detail {
    border: 2px solid transparent;
    border-radius: 50%;
    cursor: pointer;
    background-size: contain;
  }

  .sc-circle {
    width: 44px;
    height: 44px;
    overflow: hidden;
  }

  .sc-circle img {
    width: 100%;
    height: 100%;
    transform: scale(1.6) translate(10px, 3px);
  }

  .sc-circle-detail {
    width: 50px;
    height: 50px;
  }

  .sc-circle.selected,
  .sc-circle-detail.selected,
  .sc-thumb.selected {
    border-color: #87afdb;
  }

  .sc-thumb {
    width: 50px;
    height: 50px;
    border: 2px solid transparent;
    border-radius: 8px;
    cursor: pointer;
  }

  .sc-modules {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 13px;
    justify-items: center;
  }

  .sc-module {
    text-align: center;
    font-size: 14px;
    width: 140px;
    height: 158px;
  }

  .sc-module.disabled {
    opacity: 0.2;
    cursor: no-drop;
  }

  .sc-module img {
    max-width: 100%;
    border-radius: 8px;
  }

  .sc-module-dimension {
    color: #87afdb;
    font-size: 13px;
  }

  .sc-module-name {
    font-size: 14.86px;
    line-height: 100%;
  }

  .sc-modal {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    opacity: 0;
    background-color: #00000070;
    animation: enter 0.15s ease-in-out forwards;
  }

  .sc-modal-content {
    width: 600px;
    max-width: 100vw;
    height: 100%;
    background-color: white;
    float: right;
    border-radius: 10px 0 0 10px;
    overflow: auto;
    animation: slideInFromRight 0.3s ease-in-out;
  }

  .sc-modal-content.closing {
    animation: slideOutToRight 0.3s ease-in-out;
  }

  .sc-modal-inner {
    padding: 40px 20px 20px;
    display: flex;
    flex-direction: column;
  }

  .sc-modal-exit {
    position: absolute;
    top: 0;
    right: 0;
    padding: 10px;
    z-index: 1;
    cursor: pointer;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.9);
    font-size: 18px;
    font-weight: bold;
    color: #666;
    transition: all 0.2s ease;
  }

  .sc-modal-exit:hover {
    background: rgba(255, 255, 255, 1);
    color: #333;
  }

  .sc-material-header,
  .sc-material-description {
    margin-bottom: 20px;
  }

  .sc-material-image {
    display: flex;
    justify-content: center;
    padding-top: 20px;
  }

  .sc-material-image img {
    width: 400px;
    height: 400px;
  }

  .sc-apply-button {
    position: sticky;
    bottom: 0;
    background: white;
    padding: 20px 24px;
    border-top: 1px solid #ddd;
    margin: 0 -20px -20px -20px;
  }

  .sc-apply-btn {
    width: 100%;
    background: #325a82;
    color: white;
    border: none;
    padding: 14px 24px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .sc-apply-btn:hover {
    background: #2a4d70;
  }

  .sc-apply-btn:disabled {
    background: #ccc;
    cursor: not-allowed;
  }

  @keyframes enter {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideInFromRight {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }

  @keyframes slideOutToRight {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(100%);
    }
  }
`;

@customElement("sugar-configurator-side")
@observeContext("model", "refresh", "selectedModel")
export class SugarConfiguratorSide extends BaseElement {
  static styles = [styles];
  protected useShadowRoot = false;

  private _model?: CONFIGURATOR_MODEL_TYPE;

  private _materialsConfig?: {
    name: string;
    code: string;
    selected: boolean;
    materials: any[];
    materialGroups: any[];
  }[];
  private _showAll = false;
  private _isClosing = false;
  private _selectedMaterialGroup: any = undefined;
  private _selectedMaterial: any = undefined;
  private _tempSelectedMaterial: any = undefined;
  private _productModels?: CONFIGURATOR_MODEL_TYPE["models"];

  private get _configData() {
    const id = (this as any).context?.configuratorId ?? "default";
    return getConfiguratorData(id);
  }

  onContextChanged(_: any): void {}

  private _initialConfigSynced = false;
  firstUpdated(changedProperties: Map<PropertyKey, unknown>) {
    super.firstUpdated?.(changedProperties);
    this._syncInitialConfigOnce();
  }

  private _syncInitialConfigOnce() {
    if (this._initialConfigSynced) return;
    if (!this._model || !this._configData) return;
    this._initialConfigSynced = true;
    this.updatePartConfig();
    this.updateMaterialConfig();
  }

  onRefreshChanged(refresh: any) {
    this.requestUpdate();
  }

  onSelectedModelChanged(model: SugarModel) {
    const configurator = (model?.configurator ||
      model) as SugarModelConfigurator;

    if (configurator) {
      this._materialsConfig?.forEach((m) => {
        const mm = configurator.materialConfig?.get(m.code);
        m.materials.forEach((mmm) => (mmm.selected = false));
        const m1m1 = m.materials.find((mmm) => mmm.id == mm?.material.id);
        if (m1m1) m1m1.selected = true;
      });

      const side = configurator.sideConfig?.get("side");
      this._clickSideOption =
        this._configData.configurator.sides?.findIndex(
          (e) => e.name === side?.name,
        ) || 0;

      const leg = configurator.mainConfig?.get("legIn");
      this._clickLegOption =
        this._configData.configurator.legs?.findIndex(
          (e) => e.name === leg?.name,
        ) || 0;

      const back = configurator.mainConfig?.get("backOut");
      this._clickBackOption =
        this._configData.configurator.backs?.findIndex(
          (e) => e.name === back?.name,
        ) || 0;
    }

    this.requestUpdate();
  }

  onModelChanged(model: CONFIGURATOR_MODEL_TYPE) {
    this._model = model;
    this._productModels = model.models.filter(
      (m) => !this._configData.configurator.excludeModels?.includes(m.id),
    );
    this._materialsConfig = this._model?.materialPartGroups
      ?.map((mg: any) => {
        return {
          name: mg.title,
          code: mg.code,
          materials: mg.materialGroups.reduce((prev: [], current: any) => {
            return [...prev, ...current.materials];
          }, []),
          materialGroups: mg.materialGroups,
          selected: false,
        };
      })
      .sort()
      .reverse();

    this._materialsConfig?.forEach((e) => {
      (e.materials.filter((m) => m)).length > 0 ? (e.materials.filter((m) => m)[0].selected = true) : null;
    });
    this._initialConfigSynced = true;
    this.updateMaterialConfig();
    this.updatePartConfig();
    this.requestUpdate();
  }

  private _handleMaterialClick = (material: any, group: any, event?: Event) => {
    if (event) event.stopPropagation();
    this._tempSelectedMaterial = material;
    this._selectedMaterial = material;
    if (!this._showAll) {
      this._selectedMaterialGroup = group;
      material.selected = true;
      this._handleApplyMaterial();
    }

    this.requestUpdate();
  };

  private updateMaterialConfig() {
    const _defaultMaterialConfig = this._materialsConfig?.map((e) => {
      return {
        code: e.code,
        material: e.materials.find((m: any) => m.selected),
      };
    });
    (this as any).context.changeMaterial(_defaultMaterialConfig);
  }

  private updatePartConfig() {
    const legPart = this._configData.configurator.legs?.[this._clickLegOption];
    if (legPart) {
      (this as any).context.changePart(legPart);
    }

    const backPart =
      this._configData.configurator.backs?.[this._clickBackOption];
    if (backPart) {
      (this as any).context.changePart(backPart);
    }

    const sidePart =
      this._configData.configurator.sides?.[this._clickSideOption];
    if (sidePart) {
      (this as any).context.changeSidePart(sidePart);
    }
  }

  private _handleDragStart = (event: DragEvent, model: any) => {
    if (event.dataTransfer && model) {
      event.dataTransfer.clearData();
      event.dataTransfer.setData("data-model", JSON.stringify(model));
      event.dataTransfer.effectAllowed = "move";
    }
  };

  private _handleShowMaterialGroup(group: any) {
    this._showAll = true;
    this._selectedMaterialGroup = group;
    this._selectedMaterial =
      group.materials.find((m: any) => m.selected) || group.materials[0];
    this._tempSelectedMaterial = this._selectedMaterial;
    this.requestUpdate();
  }

  private _handleModalOverlayClick(event: Event) {
    if (event.target === event.currentTarget) {
      this._handleExit();
    }
  }

  private _handleExit() {
    this._isClosing = true;
    this.requestUpdate();

    setTimeout(() => {
      this._showAll = false;
      this._isClosing = false;
      this._selectedMaterialGroup = undefined;
      this._selectedMaterial = undefined;
      this._tempSelectedMaterial = undefined;
      this.requestUpdate();
    }, 300);
  }

  private _handleApplyMaterial() {
    if (this._tempSelectedMaterial && this._selectedMaterialGroup) {
      this._selectedMaterialGroup.materials.forEach(
        (m: any) => (m.selected = false),
      );
      this._tempSelectedMaterial.selected = true;

      // Sadece modal açıkken (yan menü açıkken) materyali en başa at
      if (this._showAll) {
        const materialIndex = this._selectedMaterialGroup.materials.indexOf(
          this._tempSelectedMaterial,
        );
        if (materialIndex > 0) {
          this._selectedMaterialGroup.materials.splice(materialIndex, 1);
          this._selectedMaterialGroup.materials.unshift(
            this._tempSelectedMaterial,
          );
        }
      }

      this.updateMaterialConfig();
      this._handleExit();
    }
  }

  private _clickBackOption = 0;
  private _onClickBackOption(clickOption: number) {
    this._clickBackOption = clickOption;
    (this as any).context.changePart(
      this._configData.configurator.backs?.[clickOption],
    );
    this.requestUpdate();
  }

  private _clickLegOption = 0;
  private _onClickLegOption(clickOption: number) {
    this._clickLegOption = clickOption;
    (this as any).context.changePart(
      this._configData.configurator.legs?.[clickOption],
    );
    this.requestUpdate();
  }

  private _clickSideOption = 0;
  private _onClickSideOption(clickOption: number) {
    this._clickSideOption = clickOption;
    (this as any).context.changeSidePart(
      this._configData.configurator.sides?.[clickOption],
    );
    this.requestUpdate();
  }

  protected isDisabled(model: any): boolean | undefined {
    let hasJointInscene = true;
    const outTypes =
      model.jointInfo?.modelJoints?.map((mj: any) => mj.outType) || [];
    const sceneOuttypes = (this as any).context.products?.reduce(
      (prev: [], sceneModel: SugarModel) => {
        let flatJoints = sceneModel.joints
          ?.filter((j) => !j.binded)
          ?.map((j) => j.userData.info);
        if (flatJoints) return [...prev, ...flatJoints];
        return prev;
      },
      [],
    );
    hasJointInscene = sceneOuttypes?.find((so: any) =>
      outTypes.includes(so.inType),
    );
    return model.replaceType === 1 && !hasJointInscene;
  }

  protected renderModule(model: any, disabled = false) {
    return html`
      <div class="sc-module ${disabled ? "disabled" : ""}">
        <img
          src="${model.thumbnailFileUrl}"
          alt="${t(model.name)}"
          draggable=${!disabled}
          @dragstart=${!disabled
            ? (e: any) => {
                this._handleDragStart(e, model);
              }
            : undefined}
        />
        <div class="sc-module-name">${t(model.name)}</div>
        <div class="sc-module-dimension">${model.size}</div>
      </div>
    `;
  }

  protected getLocale(name: string | Record<string, string>, l = "TR") {
    if (typeof name === "string") return name;
    return name[l];
  }

  protected renderHtml(): TemplateResult {
    return html`
      <div>
        ${this._materialsConfig?.map(
          (group) => html`
            <div class="sc-section">
              <h3 class="sc-title">
                ${t(group.name)} -
                <span
                  >${group.name === "Ahşap Seçeneği"
                    ? t(
                        this._configData.configurator.legs?.[
                          this._clickLegOption
                        ]?.name,
                      )
                    : ""}
                  /
                  ${t(
                    group.materials
                      ?.find((e) => e.selected)
                      ?.name?.split("-")[0],
                  )}-
                  ${t(
                    group.materials
                      ?.find((e) => e.selected)
                      ?.name?.split("-")[1],
                  )}</span
                >
              </h3>

              <div class="sc-options">
                ${group.name === "Ahşap Seçeneği"
                  ? this._configData.configurator.legs?.map(
                      (legOption, index) => html`
                        <div
                          class="sc-circle ${this._clickLegOption == index
                            ? "selected"
                            : ""}"
                          @click=${() => this._onClickLegOption(index)}
                        >
                          <img
                            src="icons/${legOption.imageURL}.png"
                            alt="${legOption.name || ""}"
                            style="transform:scale(1.6)"
                            loading="lazy"
                          />
                        </div>
                      `,
                    )
                  : html``}
                ${group.materials
                  .slice(0, 5)
                  .map(
                    (mat: any) => html`
                      <div
                        class="sc-circle ${mat.selected ? "selected" : ""}"
                        style="${mat.thumbnailFile ? `background-image:url(${mat.thumbnailFile});` : `background-color:#${mat.color};`}"
                        @click=${() => this._handleMaterialClick(mat, group)}
                      ></div>
                    `,
                  )}
              </div>
              ${group.materials.length > 5
                ? html`<div
                    class="sc-arrow"
                    @click=${() => this._handleShowMaterialGroup(group)}
                  >
                    <svg
                      width="12"
                      height="21"
                      viewBox="0 0 8 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0.9925 0.992188L7 6.99969L0.9925 13.0072"
                        stroke="black"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>`
                : null}
            </div>
          `,
        )}
        ${this._configData.configurator.backs &&
        this._configData.configurator.backs.length > 0
          ? html`
              <div class="sc-section">
                <h3 class="sc-title">
                  ${t("side.backCushion")} -
                  <span
                    >${t(
                      this._configData.configurator.backs?.[
                        this._clickBackOption
                      ]?.name,
                    )}</span
                  >
                </h3>

                <div class="sc-options">
                  ${this._configData.configurator.backs?.map(
                    (backOption, index) => html`
                      <div
                        class="sc-circle
               ${this._clickBackOption == index ? "selected" : ""}"
                        @click=${() => {
                          this._onClickBackOption(index);
                        }}
                      >
                        <img
                          src="icons/${backOption.imageURL}.png"
                          alt="${backOption.name || ""}"
                          loading="lazy"
                        />
                      </div>
                    `,
                  )}
                </div>
              </div>
            `
          : null}
        ${this._configData.configurator.sides &&
        this._configData.configurator.sides.length > 0
          ? html` <div class="sc-section">
              <h3 class="sc-title">
                ${t("side.armOption")} -
                <span
                  >${t(
                    this._configData.configurator.sides?.[
                      this._clickSideOption
                    ]?.name,
                  )}</span
                >
              </h3>

              <div class="sc-options">
                ${this._configData.configurator.sides?.map(
                  (sideOption, index) =>
                    html`<div>
                      <div
                        class="sc-circle
               ${this._clickSideOption == index ? "selected" : ""}"
                        @click=${() => {
                          this._onClickSideOption(index);
                        }}
                      >
                        <img
                          src="icons/${sideOption.imageURL}.png"
                          alt="${sideOption.name || ""}"
                          loading="lazy"
                        />
                      </div>
                      <div class="sc-option-title">${t(sideOption.name)}</div>
                    </div>`,
                )}
              </div>
            </div>`
          : null}

        <div class="sc-section">
          <div class="sc-modules">
            ${this._productModels
              ?.filter((model) => model && model.id)
              ?.map((model, index) => this.renderModule(model, false))}
          </div>
        </div>

        ${this._showAll && this._selectedMaterialGroup && this._selectedMaterial
          ? html`
              <div class="sc-modal" @click=${this._handleModalOverlayClick}>
                <div class="sc-modal-exit" @click=${this._handleExit}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M18 6L6 18"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M6 6L18 18"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <div
                  class="sc-modal-content ${this._isClosing ? "closing" : ""}"
                  @click=${(e: Event) => e.stopPropagation()}
                >
                  <div class="sc-modal-inner">
                    ${this._selectedMaterialGroup.materialGroups.map(
                      (matGroup: any) => html`
                          <div class="sc-section">
                          <h3 class="sc-title header">
                        ${t(matGroup.name)}
                      </h3>
                            <div class="sc-options">
                              ${matGroup.materials?.map(
                                (mat: any) =>
                                  html`<div>
                                    <div
                                      class="sc-circle-detail ${this
                                        ._tempSelectedMaterial === mat
                                        ? "selected"
                                        : ""}"
                                      style="${mat.thumbnailFile ? `background-image:url(${mat.thumbnailFile});` : `background-color:#${mat.color};`}"
                                      @click=${(e: Event) => {
                                        e.stopPropagation();
                                        this._handleMaterialClick(
                                          mat,
                                          this._selectedMaterialGroup,
                                          e,
                                        );
                                      }}
                                    ></div>
                                    <div class="sc-option-title">
                                      ${t(mat.name.split("-")[1])}
                                    </div>
                                  </div>`,
                              )}
                            </div>
                          </div>
                          </div>
                        `,
                    )}

                    <div class="sc-section">
                      <h3 class="sc-title header">
                        ${t(this._selectedMaterial.name?.split("-")[0])} -
                        ${t(this._selectedMaterial.name?.split("-")[1])}
                      </h3>
                      <div class="sc-description">
                        ${t("side.fabricDetails")}
                      </div>
                      <div class="sc-material-image">
                        ${this._selectedMaterial.thumbnailFile
                          ? html`<img src="${this._selectedMaterial.thumbnailFile}" />`
                          : html`<div style="width:100%;height:100%;background-color:#${this._selectedMaterial.color};"></div>`}
                      </div>
                    </div>

                    <div class="sc-apply-button">
                      <button
                        class="sc-apply-btn"
                        @click=${this._handleApplyMaterial}
                        ?disabled=${!this._tempSelectedMaterial}
                      >
                        ${t("side.apply")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            `
          : null}
      </div>
    `;
  }
}
